import React, { useEffect, useState } from "react";
import { ensurePushRegistered, pushPermission, pushSupported } from "../lib/push";
import { useT } from "../context/LanguageContext";
import { toast } from "sonner";
import { Bell, BellOff, BellRing, X, Loader2 } from "lucide-react";

const DISMISS_KEY = "eplay_push_optin_dismissed";

/**
 * V2 home opt-in card for push notifications.
 *
 * Lifecycle:
 *   - Not supported (e.g., iOS Safari without Add-to-Home, very old browser)
 *     → renders nothing.
 *   - Permission "granted" → renders nothing (already opted in).
 *   - Permission "denied" → renders nothing (would just nag; user has to fix
 *     in browser settings).
 *   - Permission "default" + not dismissed → renders the card.
 *   - User dismissed → renders nothing for 14 days (timestamp in localStorage).
 *
 * On click, we delegate to `ensurePushRegistered` which:
 *   1. Prompts native permission dialog
 *   2. Registers /firebase-messaging-sw.js service worker
 *   3. Gets FCM token via VAPID
 *   4. POSTs to /push/register so backend can push to this device
 *
 * After grant or deny, we hide the card so it never reappears in the same
 * session even if React re-mounts.
 */
export default function PushOptInCard() {
  const { t } = useT();
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!pushSupported()) return;
    const perm = pushPermission();
    if (perm === "granted" || perm === "denied" || perm === "unsupported") return;

    // Respect a 14-day cooldown after dismissal so we don't nag.
    try {
      const dismissedAt = parseInt(window.localStorage.getItem(DISMISS_KEY) || "0", 10);
      if (dismissedAt && Date.now() - dismissedAt < 14 * 24 * 3600 * 1000) return;
    } catch { /* noop */ }

    setVisible(true);
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    try { window.localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    setVisible(false);
  };

  const enable = async () => {
    setBusy(true);
    try {
      const token = await ensurePushRegistered({ silent: true });
      if (token) {
        toast.success(t("v2.push.toast.on"));
        setVisible(false);
      } else {
        // Permission denied or browser doesn't support — hide card either way.
        const perm = pushPermission();
        if (perm === "denied") {
          toast.error(t("v2.push.toast.denied"));
        } else {
          toast.error(t("v2.push.toast.failed"));
        }
        // Don't reset dismiss cookie — user explicitly tried, so don't keep
        // pestering them. Same 14-day cooldown applies via dismiss().
        dismiss();
      }
    } catch (e) {
      toast.error(t("v2.push.toast.failed"));
      dismiss();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="v2-push-optin-card"
      className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#7C3AED]/15 via-[#3B82F6]/10 to-[#0F0F18] border border-[#7C3AED]/30 p-4 shadow-lg shadow-[#7C3AED]/10"
    >
      <button
        type="button"
        onClick={dismiss}
        data-testid="v2-push-optin-dismiss"
        className="absolute top-2 right-2 p-1 text-[#71717A] hover:text-white rounded-full hover:bg-white/5 transition-colors"
        aria-label={t("v2.push.dismiss")}
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-start gap-3 pr-6">
        <div className="flex-shrink-0 w-11 h-11 rounded-2xl bg-gradient-to-br from-[#7C3AED] to-[#3B82F6] flex items-center justify-center shadow-lg shadow-[#7C3AED]/40">
          <BellRing className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#A5B4FC]">{t("v2.push.kicker")}</div>
          <h3 className="text-sm font-bold text-white mt-0.5">{t("v2.push.title")}</h3>
          <p className="text-xs text-[#A1A1AA] mt-1 leading-relaxed">{t("v2.push.body")}</p>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={dismiss}
          data-testid="v2-push-optin-later"
          className="rounded-xl border border-[#1F1F2A] text-[#A1A1AA] font-bold text-xs py-2.5 hover:border-white hover:text-white transition-colors"
        >
          {t("v2.push.later")}
        </button>
        <button
          type="button"
          onClick={enable}
          disabled={busy}
          data-testid="v2-push-optin-enable"
          className="rounded-xl bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] text-white font-bold text-xs py-2.5 shadow-lg shadow-[#7C3AED]/30 hover:opacity-95 disabled:opacity-50 transition-opacity inline-flex items-center justify-center gap-1.5"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />}
          {busy ? t("v2.push.busy") : t("v2.push.enable")}
        </button>
      </div>
    </section>
  );
}
